"use client";

import { Fragment, memo, useEffect, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { JobType, MoneyUnit } from "@/modules/types/enum";
import clsx from "clsx";
import {
  ArrowDown01Icon,
  Cancel01Icon,
  PreferenceVerticalIcon,
  Search01Icon,
  Tick01Icon,
} from "hugeicons-react";
import { useClientTranslations } from "@/lib/translate";
import { useLocation } from "@/modules/hooks/metadata/useLocation";
import { Category, Level, Province } from "@/modules/types/metadata/metadata";
import { useMetadata } from "@/modules/hooks/metadata/useMetadata";
import { useRouter, useSearchParams } from "next/navigation";
import { routes } from "@/lib/utils/routes";

const SALARY_RANGES = [
  {
    id: "duoi-5-trieu",
    name: "Dưới 5 triệu",
    min: 0,
    max: 5000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-5-den-10-trieu",
    name: "Từ 5 đến 10 triệu",
    min: 5000000,
    max: 10000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-10-den-15-trieu",
    name: "Từ 10 đến 15 triệu",
    min: 10000000,
    max: 15000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-15-den-20-trieu",
    name: "Từ 15 đến 20 triệu",
    min: 15000000,
    max: 20000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-20-den-25-trieu",
    name: "Từ 20 đến 25 triệu",
    min: 20000000,
    max: 25000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-25-den-30-trieu",
    name: "Từ 25 đến 30 triệu",
    min: 25000000,
    max: 30000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-30-den-35-trieu",
    name: "Từ 30 đến 35 triệu",
    min: 30000000,
    max: 35000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-35-den-40-trieu",
    name: "Từ 35 đến 40 triệu",
    min: 35000000,
    max: 40000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tu-40-den-50-trieu",
    name: "Từ 40 đến 50 triệu",
    min: 40000000,
    max: 50000000,
    moneyUnit: MoneyUnit.vnd,
  },
  {
    id: "tren-50-trieu",
    name: "Trên 50 triệu",
    min: 50000000,
    max: null,
    moneyUnit: MoneyUnit.vnd,
  },
];

const EXPERIENCE_YEARS = [
  { id: "khong-kinh-nghiem", name: "noexperience" },
  { id: "1-2-nam", name: "12years" },
  { id: "3-5-nam", name: "35years" },
  { id: "5-7-nam", name: "57years" },
  { id: "tren-7-nam", name: "over7years" },
];

const JOB_TYPES = Object.values(JobType).map((type) => type);

export const JobFilter = memo(() => {
  const { t: metaT } = useClientTranslations("metadata");
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());
  const { provinces, searchProvinces, resetProvinces } = useLocation();
  const { levels, categories } = useMetadata();
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProvince, setSelectedProvince] = useState<Province | null>(
    null
  );
  const [selectedJobType, setSelectedJobType] = useState<JobType | null>(null);
  const [selectedSalaryRange, setSelectedSalaryRange] = useState<{
    id: string;
    name: string;
    min: number;
    max: number | null;
    moneyUnit: MoneyUnit;
  } | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  const handleClearFilters = () => {
    router.push(`${routes.job.index}`);
  };

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q.length > 0) {
      setSearch(q);
    }
    const jt = searchParams.get("jt");
    if (jt && jt.length > 0) {
      const jobType = JOB_TYPES.find((jobType) => jobType === jt);
      if (jobType) {
        setSelectedJobType(jobType);
      }
    }
    const sr = searchParams.get("sr");
    if (sr && sr.length > 0) {
      const salaryRange = SALARY_RANGES.find((salary) => salary.id === sr);
      if (salaryRange) {
        setSelectedSalaryRange(salaryRange);
      }
    }
    const lv = searchParams.get("lv");
    if (lv && lv.length > 0) {
      const level = levels.find((level) => level.title === lv);
      if (level) {
        setSelectedLevel(level);
      }
    }
    const exp = searchParams.get("exp");
    if (exp && exp.length > 0) {
      const experience = EXPERIENCE_YEARS.find(
        (experience) => experience.id === exp
      );
      if (experience) {
        setSelectedExperience(experience);
      }
    }
    const ct = searchParams.get("ct");
    if (ct && ct.length > 0) {
      const category = categories.find((category) => category.slug === ct);
      if (category) {
        setSelectedCategory(category);
      }
    }
    const prv = searchParams.get("prv");
    if (prv && prv.length > 0) {
      const province = provinces.find((province) => province.codeName === prv);
      if (province) {
        setSelectedProvince(province);
      }
    }
  }, [provinces, levels, categories]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onFilterJobs();
  };

  const onFilterJobs = () => {
    params.set("q", search);
    if (selectedProvince) {
      params.set("prv", selectedProvince.codeName);
    }
    if (selectedJobType) {
      params.set("jt", selectedJobType);
    }
    if (selectedSalaryRange) {
      params.set("sr", selectedSalaryRange.id);
    }
    if (selectedLevel) {
      params.set("lv", selectedLevel.title);
    }
    if (selectedExperience) {
      params.set("exp", selectedExperience.id);
    }
    if (selectedCategory && selectedCategory.slug) {
      params.set("ct", selectedCategory.slug);
    }

    router.push(`${routes.job.index}?${params.toString()}`);
  };

  return (
    <section className="flex justify-center bg-white">
      <form
        onSubmit={onSubmit}
        className="container py-6 px-2 md:px-8 transition-all duration-300"
      >
        <div className="grid grid-cols-12 gap-4">
          {/* City Combobox */}
          <div className="relative col-span-12 sm:col-span-3">
            <Combobox
              value={selectedProvince}
              onChange={(province) => {
                setSelectedProvince(province);
                resetProvinces();
              }}
            >
              <div className="relative col-span-3">
                <ComboboxInput
                  className="w-full rounded-lg border-none bg-white/5 py-1.5 sm:py-3 pr-8 pl-3 text-sm/6 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
                  displayValue={(province: Province) => province?.fullName}
                  placeholder={metaT("selectProvince")}
                  onChange={(event) => searchProvinces(event.target.value)}
                />
                <ComboboxButton
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-2"
                >
                  <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                </ComboboxButton>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
              >
                <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {provinces.map((province) => (
                    <ComboboxOption
                      key={province.code}
                      value={province}
                      className={({ active }) =>
                        clsx(
                          "relative cursor-pointer select-none py-2 pl-8 pr-4",
                          active ? "text-primary" : "text-gray-900"
                        )
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={
                              selected
                                ? "font-semibold text-primary"
                                : "font-normal"
                            }
                          >
                            {province.fullName}
                          </span>
                          {selected && (
                            <span
                              className={clsx(
                                "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                              )}
                            >
                              <Tick01Icon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </ComboboxOption>
                  ))}
                </ComboboxOptions>
              </Transition>
            </Combobox>
          </div>
          {/* Search Input */}
          <div className="col-span-12 sm:col-span-6">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Enter keyword skill, job title, company..."
              className="w-full rounded-lg border-none bg-white/5 py-1.5 sm:py-3 pr-8 pl-3 text-sm/6 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="col-span-6 sm:hidden">
            <button
              type="button"
              onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
              className="flex justify-center items-center w-full rounded-lg border-none bg-white/5 py-1.5 sm:py-3 text-sm/6 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary"
            >
              <PreferenceVerticalIcon className="size-6" />
            </button>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <button
              type="submit"
              className="flex justify-center items-center gap-3 w-full rounded-lg border-none bg-primary hover:bg-primaryDark transition-colors duration-300 py-1.5 sm:py-3 text-sm/6 text-primaryForeground ring-1 ring-inset ring-primaryLight focus:ring-2 focus:ring-primary"
            >
              <Search01Icon className="size-5" /> Search
            </button>
          </div>

          <div
            className={clsx(
              "col-span-12 sm:grid grid-cols-7 gap-3",
              "transition-all duration-300",
              isAdvancedFilterOpen ? "grid !grid-cols-2" : "hidden" // Toggle on mobile
            )}
          >
            {/* Job Type Listbox */}
            <div className="relative">
              <Listbox value={selectedJobType} onChange={setSelectedJobType}>
                <ListboxButton
                  type="button"
                  className="relative w-full rounded-lg border-none transition-colors duration-150 bg-gray-100 hover:bg-gray-200 hover:text-primary py-1.5 sm:py-3 pr-8 pl-3 text-left text-sm/6 text-gray-900 focus:ring-2 focus:ring-primary"
                >
                  <span className="block truncate">
                    {selectedJobType
                      ? metaT(`jobType.${selectedJobType}`)
                      : metaT("selectJobType")}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                >
                  <ListboxOptions
                    className={clsx(
                      "absolute z-10 mt-1 max-h-60 min-w-96 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:var(--spacing-1)]",
                      "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                    )}
                  >
                    {JOB_TYPES.map((type) => (
                      <ListboxOption
                        key={type}
                        value={type}
                        className={({ active }) =>
                          clsx(
                            "relative select-none py-2 pl-8 pr-4 cursor-pointer",
                            active ? "text-primary" : "text-gray-900"
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={
                                selected
                                  ? "font-semibold text-primary"
                                  : "font-normal"
                              }
                            >
                              {metaT(`jobType.${type}`)}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                                )}
                              >
                                <Tick01Icon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </Listbox>
            </div>

            {/* Salary Range */}
            <div className="relative">
              <Listbox
                value={selectedSalaryRange}
                onChange={setSelectedSalaryRange}
              >
                <ListboxButton
                  type="button"
                  className="relative w-full rounded-lg border-none transition-colors duration-150 bg-gray-100 hover:bg-gray-200 hover:text-primary py-1.5 sm:py-3 pr-8 pl-3 text-left text-sm/6 text-gray-900"
                >
                  <span className="block truncate">
                    {selectedSalaryRange
                      ? selectedSalaryRange.name
                      : metaT("selectSalaryRange")}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                >
                  <ListboxOptions
                    anchor="bottom end"
                    className={clsx(
                      "absolute z-10 mt-1 max-h-fit min-w-96 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:var(--spacing-1)]",
                      "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                    )}
                  >
                    {SALARY_RANGES.map((type) => (
                      <ListboxOption
                        key={type.id}
                        value={type}
                        className={({ active }) =>
                          clsx(
                            "relative select-none py-2 pl-8 pr-4 cursor-pointer",
                            active ? "text-primary" : "text-gray-900"
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={
                                selected
                                  ? "font-semibold text-primary"
                                  : "font-normal"
                              }
                            >
                              {type.name}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                                )}
                              >
                                <Tick01Icon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </Listbox>
            </div>

            {/* Level */}
            <div className="relative">
              <Listbox value={selectedLevel} onChange={setSelectedLevel}>
                <ListboxButton
                  type="button"
                  className="relative w-full rounded-lg border-none transition-colors duration-150 bg-gray-100 hover:bg-gray-200 hover:text-primary py-1.5 sm:py-3 pr-8 pl-3 text-left text-sm/6 text-gray-900"
                >
                  <span className="block truncate">
                    {selectedLevel ? selectedLevel.title : metaT("selectLevel")}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                >
                  <ListboxOptions
                    className={clsx(
                      "absolute z-10 mt-1 max-h-60 min-w-60 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:var(--spacing-1)]",
                      "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                    )}
                  >
                    {levels.map((type) => (
                      <ListboxOption
                        key={type.id}
                        value={type}
                        className={({ active }) =>
                          clsx(
                            "relative select-none py-2 pl-8 pr-4 cursor-pointer",
                            active ? "text-primary" : "text-gray-900"
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={
                                selected
                                  ? "font-semibold text-primary"
                                  : "font-normal"
                              }
                            >
                              {type.title}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                                )}
                              >
                                <Tick01Icon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </Listbox>
            </div>

            {/* Experience */}
            <div className="relative">
              <Listbox
                value={selectedExperience}
                onChange={setSelectedExperience}
              >
                <ListboxButton
                  type="button"
                  className="relative w-full rounded-lg border-none transition-colors duration-150 bg-gray-100 hover:bg-gray-200 hover:text-primary py-1.5 sm:py-3 pr-8 pl-3 text-left text-sm/6 text-gray-900"
                >
                  <span className="block truncate">
                    {selectedExperience
                      ? metaT(`yearExp.${selectedExperience.name}`)
                      : metaT("selectYearExp")}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                >
                  <ListboxOptions
                    className={clsx(
                      "absolute z-10 mt-1 max-h-60 min-w-fit overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:var(--spacing-1)]",
                      "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                    )}
                  >
                    {EXPERIENCE_YEARS.map((type) => (
                      <ListboxOption
                        key={type.id}
                        value={type}
                        className={({ active }) =>
                          clsx(
                            "relative select-none py-2 pl-8 pr-4 cursor-pointer",
                            active ? "text-primary" : "text-gray-900"
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={
                                selected
                                  ? "font-semibold text-primary"
                                  : "font-normal"
                              }
                            >
                              {metaT(`yearExp.${type.name}`)}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                                )}
                              >
                                <Tick01Icon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </Listbox>
            </div>

            {/* Category */}
            <div className="relative">
              <Listbox value={selectedCategory} onChange={setSelectedCategory}>
                <ListboxButton
                  type="button"
                  className="relative w-full rounded-lg border-none transition-colors duration-150 bg-gray-100 hover:bg-gray-200 hover:text-primary py-1.5 sm:py-3 pr-8 pl-3 text-left text-sm/6 text-gray-900"
                >
                  <span className="block truncate">
                    {selectedCategory?.title || metaT("selectCategory")}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ArrowDown01Icon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                >
                  <ListboxOptions
                    anchor="bottom start"
                    className={clsx(
                      "absolute z-10 mt-1 max-h-60 min-w-96 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:var(--spacing-1)]",
                      "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                    )}
                  >
                    {categories.map((type) => (
                      <ListboxOption
                        key={type.id}
                        value={type}
                        className={({ active }) =>
                          clsx(
                            "relative select-none py-2 pl-8 pr-4 cursor-pointer",
                            active ? "text-primary" : "text-gray-900"
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            {type.icon && (
                              <span className="mr-2">
                                <i className={`${type.icon}`} />
                              </span>
                            )}
                            <span
                              className={
                                selected
                                  ? "font-semibold text-primary"
                                  : "font-normal"
                              }
                            >
                              {type.title}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 left-0 flex items-center pl-2 text-primary"
                                )}
                              >
                                <Tick01Icon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </Listbox>
            </div>

            {/* Clear Filters Button */}
            <div className="col-span-1 sm:col-span-2 h-full flex justify-end">
              <button
                type="button"
                onClick={handleClearFilters}
                className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <Cancel01Icon className="w-5 h-5 mr-2" />
                Clear Filters
              </button>
            </div>
            {/* Similar structure for other Listbox components... */}
            {/* Copy the Listbox structure above and modify for , and Category */}
          </div>
        </div>
      </form>
    </section>
  );
});

JobFilter.displayName = "JobFilter";

export const JobFilterLoading = () => {
  return (
    <section className="flex justify-center bg-white">
      <div className="container py-6 px-2 md:px-8 transition-all duration-300">
        <div className="grid grid-cols-12 gap-4">
          {/* City Combobox Loading */}
          <div className="col-span-12 sm:col-span-3">
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />
          </div>

          {/* Search Input Loading */}
          <div className="col-span-12 sm:col-span-6">
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />
          </div>

          {/* Mobile Filter Button Loading */}
          <div className="col-span-6 sm:hidden">
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />
          </div>

          {/* Search Button Loading */}
          <div className="col-span-6 sm:col-span-3">
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />
          </div>

          {/* Advanced Filters Loading */}
          <div className="hidden col-span-12 sm:grid grid-cols-7 gap-3">
            {/* Job Type Loading */}
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />

            {/* Salary Range Loading */}
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />

            {/* Level Loading */}
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />

            {/* Experience Loading */}
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />

            {/* Category Loading */}
            <div className="h-11 w-full bg-gray-200 animate-pulse rounded-lg" />

            {/* Clear Filters Loading */}
            <div className="col-span-2 flex justify-end">
              <div className="h-11 w-32 bg-gray-200 animate-pulse rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
